export const ctaCornerStyleValues = ['square', 'rounded', 'pill'] as const;

export type CtaCornerStyle = (typeof ctaCornerStyleValues)[number];

export type CtaTheme = {
  cardColor: string;
  buttonColor: string;
  accentColor: string;
  cornerStyle: CtaCornerStyle;
};

export const DEFAULT_CTA_THEME: CtaTheme = {
  cardColor: '#120D08',
  buttonColor: '#FFFAF0',
  accentColor: '#FFE9BD',
  cornerStyle: 'pill'
};

const hexPattern = /^#[0-9a-f]{6}$/i;
const darkText = '#000000';
const lightText = '#FFFFFF';

const toChannel = (value: string, offset: number) => Number.parseInt(value.slice(offset, offset + 2), 16);

const toHex = (value: number) => Math.round(Math.max(0, Math.min(255, value))).toString(16).padStart(2, '0');

export const normalizeHexColor = (value: unknown, fallback: string) => {
  if (typeof value !== 'string' || !hexPattern.test(value.trim())) return fallback;
  return value.trim().toUpperCase();
};

export const normalizeCtaTheme = (value: unknown, fallback: CtaTheme = DEFAULT_CTA_THEME): CtaTheme => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return { ...fallback };
  const candidate = value as Record<string, unknown>;
  const cornerStyle = ctaCornerStyleValues.includes(candidate.cornerStyle as CtaCornerStyle)
    ? candidate.cornerStyle as CtaCornerStyle
    : fallback.cornerStyle;

  return {
    cardColor: normalizeHexColor(candidate.cardColor, fallback.cardColor),
    buttonColor: normalizeHexColor(candidate.buttonColor, fallback.buttonColor),
    accentColor: normalizeHexColor(candidate.accentColor, fallback.accentColor),
    cornerStyle
  };
};

export const resolveCtaTheme = (adOverride: unknown, clientTheme: unknown): CtaTheme =>
  normalizeCtaTheme(adOverride === null || adOverride === undefined ? clientTheme : adOverride);

const rgb = (hex: string) => ({
  r: toChannel(hex, 1),
  g: toChannel(hex, 3),
  b: toChannel(hex, 5)
});

const linearChannel = (channel: number) => {
  const value = channel / 255;
  return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
};

export const relativeLuminance = (hex: string) => {
  const color = rgb(normalizeHexColor(hex, '#000000'));
  return 0.2126 * linearChannel(color.r) + 0.7152 * linearChannel(color.g) + 0.0722 * linearChannel(color.b);
};

export const contrastRatio = (first: string, second: string) => {
  const firstLuminance = relativeLuminance(first);
  const secondLuminance = relativeLuminance(second);
  const lighter = Math.max(firstLuminance, secondLuminance);
  const darker = Math.min(firstLuminance, secondLuminance);
  return (lighter + 0.05) / (darker + 0.05);
};

export const readableTextColor = (background: string) => {
  const normalizedBackground = normalizeHexColor(background, DEFAULT_CTA_THEME.cardColor);
  return contrastRatio(normalizedBackground, darkText) >= contrastRatio(normalizedBackground, lightText)
    ? darkText
    : lightText;
};

export const mixHexColors = (background: string, foreground: string, foregroundWeight: number) => {
  const from = rgb(normalizeHexColor(background, '#000000'));
  const to = rgb(normalizeHexColor(foreground, '#FFFFFF'));
  const weight = Math.max(0, Math.min(1, foregroundWeight));
  return `#${toHex(from.r + (to.r - from.r) * weight)}${toHex(from.g + (to.g - from.g) * weight)}${toHex(from.b + (to.b - from.b) * weight)}`.toUpperCase();
};

const mutedReadableTextColor = (background: string, foreground: string) => {
  for (const weight of [0.72, 0.78, 0.84, 0.9, 1]) {
    const candidate = mixHexColors(background, foreground, weight);
    if (contrastRatio(background, candidate) >= 4.5) return candidate;
  }
  return foreground;
};

const rgba = (hex: string, alpha: number) => {
  const color = rgb(normalizeHexColor(hex, '#000000'));
  return `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
};

const cornerTokens: Record<CtaCornerStyle, { card: string; button: string; mark: string }> = {
  square: { card: '0.5rem', button: '0.5rem', mark: '0.5rem' },
  rounded: { card: '1.35rem', button: '1rem', mark: '0.92rem' },
  pill: { card: '1.35rem', button: '999px', mark: '0.92rem' }
};

export const ctaThemeCssVariables = (value: unknown): Record<string, string> => {
  const theme = normalizeCtaTheme(value);
  const usesLegacyPalette =
    theme.cardColor === DEFAULT_CTA_THEME.cardColor &&
    theme.buttonColor === DEFAULT_CTA_THEME.buttonColor &&
    theme.accentColor === DEFAULT_CTA_THEME.accentColor;
  const cardText = usesLegacyPalette ? '#FFFAF0' : readableTextColor(theme.cardColor);
  const buttonText = usesLegacyPalette ? '#120D08' : readableTextColor(theme.buttonColor);
  const corners = cornerTokens[theme.cornerStyle];

  return {
    '--cta-card-color': theme.cardColor,
    '--cta-card-background': usesLegacyPalette
      ? 'radial-gradient(circle at 12% 16%, rgba(255, 230, 186, 0.22), transparent 8rem), linear-gradient(145deg, rgba(28, 20, 12, 0.78), rgba(8, 8, 7, 0.84))'
      : theme.cardColor,
    '--cta-card-text': cardText,
    '--cta-card-muted-text': mutedReadableTextColor(theme.cardColor, cardText),
    '--cta-button-color': theme.buttonColor,
    '--cta-button-text': buttonText,
    '--cta-accent-color': theme.accentColor,
    '--cta-accent-border': rgba(theme.accentColor, 0.44),
    '--cta-accent-soft': rgba(theme.accentColor, 0.12),
    '--cta-card-radius': corners.card,
    '--cta-button-radius': corners.button,
    '--cta-mark-radius': corners.mark
  };
};
